<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import SpinnerIcon from "primevue/icons/spinner";
import { computed } from "vue";
import {
  addOnModalDialogPt,
  addOnRequestsPresets,
} from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/AddOnRequests/styles";
import type { AddOnRequestItem } from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/AddOnRequests/types";
import AddCircleIcon from "@/components/shared/icons/AddCircleIcon.vue";
import ClockIcon from "@/components/shared/icons/ClockIcon.vue";

defineOptions({ name: "AddOnConfirmAddModal" });

const props = withDefaults(
  defineProps<{
    visible: boolean;
    item?: AddOnRequestItem;
    saving?: boolean;
  }>(),
  {
    item: undefined,
    saving: false,
  },
);

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "confirm"): void;
}>();

const handleHide = (): void => {
  emit("update:visible", false);
};

const detailFields = computed(() =>
  [
    { key: "date", value: props.item?.dateLabel },
    { key: "location", value: props.item?.location },
    { key: "duration", value: props.item?.durationLabel },
    { key: "price", value: props.item?.priceLabel, class: "font-semibold text-indigo-base" },
  ].filter((field) => field.value),
);
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!saving"
    header="Add excursion to order"
    :pt="addOnModalDialogPt"
    :style="{ width: '560px' }"
    data-testid="add-on-confirm-add-modal"
    @update:visible="handleHide"
  >
    <p class="text-15p leading-relaxed text-gray-600 mb-4" data-testid="add-on-confirm-add-description-text">
      Add <strong class="font-semibold text-indigo-base">{{ item?.name }}</strong> to this order? It will be
      placed in the Excursions section with a status of
      <strong class="font-semibold text-indigo-base">Fulfilled</strong>.
    </p>

    <div v-if="item" class="border border-gray-175 rounded-lg bg-blue-100 p-4" data-testid="add-on-confirm-add-details">
      <p class="text-base font-semibold text-indigo-base mb-1.5">{{ item.name }}</p>
      <div class="flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <template v-for="(field, index) in detailFields" :key="field.key">
          <span v-if="index > 0" class="text-gray-400">|</span>
          <span :class="field.class">{{ field.value }}</span>
        </template>
      </div>
      <div class="flex items-center gap-2 mt-2.5 text-13p text-gray-600">
        <ClockIcon class="h-3.5 w-3.5 flex-none" />
        <span>Request submitted {{ item.requestedAtLabel }}</span>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        outlined
        :disabled="saving"
        :pt="addOnRequestsPresets.outlinedTealButton"
        data-testid="add-on-confirm-add-cancel-button"
        @click="handleHide"
      />
      <Button
        :disabled="saving"
        data-testid="add-on-confirm-add-button"
        :pt="addOnRequestsPresets.primaryButton"
        @click="emit('confirm')"
      >
        <SpinnerIcon v-if="saving" class="h-4 w-4 mr-2 animate-spin" />
        <AddCircleIcon v-else class="h-4 w-4 mr-2" />
        Add Excursion
      </Button>
    </template>
  </Dialog>
</template>
